import classNames from "classnames";
import moment from "moment-timezone";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { InputGroup, Tooltip } from "reactstrap";
import CustomTimeInput from "../../../../../components/Inputs/CustomTimeInput";
import { applyTimezone, combineDateAndTime } from "../../../../../utils/helpers/date";
import { useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { getTimeSheetTimeZone, isAddedTimeSheet, isTimeInputDisabled } from "../helpers";
import { useAddedPayCode } from "../hooks/useAddedPayCode";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { renderClockExceptionType } from "../../../Home/Actions/HistoryDrawer/ClockExceptions/useColumns";
import { getMandatoryBreak, isMandatoryBreakViolated } from "../../MandatoryBreakWarning/helpers";

export const getClockInputStyle = ({
    color,
    border,
    backgroundColor,
    value,
    original,
}) => {
    let style;
    if (value) {
        if (original.payCode?.hourType === "unpaid") {
            style = {
                color: "#00080e",
                backgroundColor: "#f2f1f3a6",
                border: `1px solid transparent`,
                padding: '0.2rem!important',
                fontSize: '0.9rem',
            };
        } else {
            style = {
                color,
                backgroundColor,
                border,
                padding: '0.2rem!important',
                fontSize: '0.9rem',
            };
        }
    } else {
        style = {
            color,
            backgroundColor: "transparent",
            border: `1px solid ${color}`,
            padding: '0.2rem!important',
            fontSize: '0.9rem',
        };
    }
    return style;
}

export const ExceptionTooltip = ({ original, view, exceptions, style }) => {
    const { t } = useTranslation();
    const { visible, toggle } = useVisible();
    const id = `exceptions${exceptions?.[0]?.id}`;

    return (
        <>
            <span id={id} className="px-1">
                <FontAwesomeIcon
                    className="text-sm cursor-pointer"
                    icon={faClockRotateLeft}
                    style={style}
                    onClick={() => view({ ...original, menu: "clock-exception" })}
                />
            </span>

            <Tooltip
                target={id}
                isOpen={visible}
                toggle={toggle}
            >
                <div className="d-flex flex-column">
                    {exceptions.map((exception) => (
                        <span className="mb-1">
                            {t(renderClockExceptionType(exception.type))}
                        </span>
                    ))}
                </div>
            </Tooltip>
        </>
    )
}

export const canTimeSheetBeBinded = (timeSheet) => {
    return timeSheet.payCode && !['amount', 'unit'].includes(timeSheet.payCode.hourType);
}

export const useInColumn = ({
    user,
    canEdit,
    clockExceptions,
    view,
    openInOutDateModal,
    openSyncTimesheetModal,
    openMandatoryBreakWarning,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef();
    const timeFormat = useCompanyTimeFormat();
    const payCode = useAddedPayCode(user);

    const style = useMemo(() => {
        if (timeFormat) {
            switch (timeFormat.length) {
                case 1:
                case 2:
                case 3:
                case 4:
                case 5:
                    return { width: '60px' };
                case 6:
                case 7:
                    return { width: '70px' };
                case 8:
                case 9:
                case 10:
                    return { width: '80px' };
                default:
                    return { width: '116px' }
            }
        }
        return { width: '116px' }
    }, [timeFormat])

    return useMemo(() => ({
        Header: t("in"),
        accessor: "startDate",
        alwaysVisible: true,
        headCustomProps: { style },
        Cell: ({ value, row, cell, updateAddedTimeSheet, updateTimeSheet, data }) => {
            const { original, isNonProductive } = row;
            const disabled = isTimeInputDisabled(original) || row.disabled || cell.disabled || isNonProductive;
            const timeZone = getTimeSheetTimeZone(original);
            const currentValue = value
                ? timeZone
                    ? moment.parseZone(value).toISOString(true)
                    : value
                : undefined;
            const style = getClockInputStyle({
                color: "#00998b",
                backgroundColor: "#00998b1a",
                border: `1px solid transparent`,
                value,
                original,
            });

            if (isNonProductive || ["unit", "amount"].includes(original?.payCode?.hourType)) {
                return "-";
            }

            let exceptions = clockExceptions?.filter((exception) => moment(original?.date, "YYYY-MM-DD").isSame(moment(exception?.date, "YYYY-MM-DD"), "date"));
            exceptions = exceptions?.map((exception) => exception?.data);
            exceptions = (exceptions || []).flat();
            exceptions = exceptions?.filter((exception) =>
            (
                [
                    "came_early",
                    "came_late"
                ].includes(exception?.type) &&
                exception?.timesheet?.id === original?.id
            )
            );
            if (openInOutDateModal && original?.startDate) {
                style.borderLeft = 0;
            }

            return (
                <div className="d-flex align-items-center">
                    <InputGroup className="custom-time-input-group">
                        {(openInOutDateModal && original?.startDate && canEdit) && (
                            <span
                                className={classNames("clock-icon text-center", value && "filled",
                                    original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                                style={{
                                    ...style,
                                    padding: "0.1rem 0.4rem",
                                }}
                            >
                                <FontAwesomeIcon
                                    className="cursor-pointer"
                                    icon={faClock}
                                    onClick={() => openInOutDateModal({
                                        column: "in",
                                        user,
                                        original,
                                        updateAddedTimeSheet,
                                        updateTimeSheet
                                    })}
                                    style={{ fontSize: "1rem", paddingTop: "0.2rem" }}
                                />
                            </span>
                        )}
                        <CustomTimeInput
                            ref={inputRef}
                            timeFormat={timeFormat}
                            className={classNames("time-input text-center", value && "filled", (value && canEdit) && "input-with-value",
                                original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                            style={style}
                            disabled={disabled || !canEdit}
                            placeholder={t("time")}
                            value={currentValue}
                            onChange={(val) => {
                                let startDate = "";
                                const updatedFields = {}
                                if (val) {
                                    startDate = combineDateAndTime(
                                        moment(original.date, "YYYY-MM-DD"),
                                        moment(val),
                                    );
                                    startDate = applyTimezone(startDate, timeZone, true);

                                    if (original.endDate) {
                                        if (startDate.date() !== moment.parseZone(original.endDate).date()) {
                                            let endDate = combineDateAndTime(
                                                moment(original.date, "YYYY-MM-DD"),
                                                moment.parseZone(original.endDate),
                                            );
                                            endDate = applyTimezone(endDate, timeZone, true);
                                            if (endDate.isAfter(startDate, "minute")) {
                                                updatedFields.endDate = endDate.toISOString(true);
                                            }
                                        }
                                        if (moment(original.endDate).isBefore(startDate)) {
                                            updatedFields.endDate = moment
                                                .parseZone(original.endDate)
                                                .add(1, "day")
                                                .toISOString(true);
                                        }
                                    }
                                    startDate = startDate.toISOString(true);
                                }

                                // update sibling
                                // ?? maybe on binded field change, open a modal to confirm the binding change
                                // ?? should bind continue if the change causes a cut / conflict / other issues
                                // TODO: bind should stop if user unbinded the timesheets
                                if (original.startDate && canTimeSheetBeBinded(original) && openSyncTimesheetModal) {
                                    const sibling = data.find((item) => {
                                        if (!item.endDate || !canTimeSheetBeBinded(item)) {
                                            return false;
                                        }
                                        if (item.id === original.id) {
                                            return false;
                                        }
                                        return moment.parseZone(item.endDate)
                                            .isSame(moment.parseZone(original.startDate), "minute");
                                    });
                                    // TODO: detect swallous / conflicting timesheets / other issues due to change / binding
                                    if (sibling) {
                                        const endDate = startDate;
                                        const onSuccess = () => {
                                            const callback = isAddedTimeSheet(sibling)
                                                ? updateAddedTimeSheet.bind(null, sibling)
                                                : updateTimeSheet.bind(null, sibling.id)
                                            callback({ endDate });
                                        }
                                        openSyncTimesheetModal({
                                            field: t("end-date").toLowerCase(),
                                            onSuccess,
                                        })
                                    }
                                }

                                const duration = original?.endDate && startDate
                                    ? moment(original.endDate).diff(moment(startDate), "seconds")
                                    : original?.duration || 0;

                                updatedFields.startDate = startDate;
                                updatedFields.duration = duration;

                                const addedTimesheet = isAddedTimeSheet(original);
                                if (addedTimesheet && !original?.payCode) {
                                    updatedFields.payCode = payCode;
                                }
                                const changeValue = (data) => {
                                    if (!val) {
                                        return;
                                    }
                                    if (isAddedTimeSheet(original)) {
                                        updateAddedTimeSheet(original, data);
                                    } else {
                                        updateTimeSheet(original.id, data);
                                    }
                                }
                                const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null;
                                if (mandatoryBreak) {
                                    const inViolation = isMandatoryBreakViolated(
                                        {
                                            ...original,
                                            ...updatedFields,
                                            payCode: original.payCode || updatedFields.payCode,
                                        },
                                        mandatoryBreak,
                                    );
                                    if (inViolation) {
                                        openMandatoryBreakWarning({
                                            column: "in",
                                            requiredTime: mandatoryBreak.duration,
                                            original,
                                            updatedFields,
                                            onConfirm: () => {
                                                changeValue(updatedFields);
                                            },
                                            onCancel: () => {
                                                // WORKAROUND FOR TIME INPUT
                                                inputRef.current?.reset();
                                            },
                                        });
                                    } else {
                                        changeValue(updatedFields);
                                    }
                                } else {
                                    changeValue(updatedFields);
                                }
                            }}
                        />
                    </InputGroup>

                    {(exceptions?.length > 0 && view) ? (
                        <ExceptionTooltip
                            original={original}
                            view={view}
                            exceptions={exceptions}
                            style={{
                                color: "#00998b",

                            }}
                        />
                    ) : null}
                </div>
            );
        },
    }), [
        t,
        style,
        clockExceptions,
        openInOutDateModal,
        timeFormat,
        canEdit,
        view,
        user,
        payCode,
        openSyncTimesheetModal,
        openMandatoryBreakWarning
    ]);
};
